/**
 * RingsLoader — concentric rotating rings spinner.
 *
 * Pure-CSS animated SVG. Three rings rotate at different speeds (and
 * one in the opposite direction) so the motion reads as "thinking"
 * without becoming hypnotic. Centre dot pulses for the same reason.
 *
 * `prefers-reduced-motion: reduce` freezes every animation — the
 * static silhouette still communicates "loading" via the centre dot
 * + rings, just without movement.
 *
 * Used by `<SplashScreen>` and any surface needing a non-skeleton
 * "thinking" indicator. Composed with semantic tokens only — no raw
 * hex.
 */

import './RingsLoader.css';

export type RingsLoaderSize = 'sm' | 'md' | 'lg';
export type RingsLoaderTone = 'brand' | 'fg-muted';

export type RingsLoaderProps = {
    /** Diameter: sm=48px, md=80px (default), lg=120px. */
    size?: RingsLoaderSize;
    /** Stroke colour ramp. Default 'brand'. */
    tone?: RingsLoaderTone;
    /** Accessible label. Default "Loading". */
    label?: string;
};

function RingsLoader({
    size = 'md',
    tone = 'brand',
    label = 'Loading',
}: RingsLoaderProps) {
    return (
        <span
            className={`ui-rings-loader ui-rings-loader--${size} ui-rings-loader--tone-${tone}`}
            role="status"
            aria-label={label}
        >
            <span className="ui-rings-loader__ring ui-rings-loader__ring--outer" aria-hidden="true" />
            <span className="ui-rings-loader__ring ui-rings-loader__ring--mid"   aria-hidden="true" />
            <span className="ui-rings-loader__ring ui-rings-loader__ring--inner" aria-hidden="true" />
            <span className="ui-rings-loader__dot" aria-hidden="true" />
        </span>
    );
}

export default RingsLoader;
